import {
  SerializedPlainDate,
  SerializedPlainDateTime,
  SerializedTime,
} from './api/data.types';
import {
  FacilityReservationParty,
  FacilityReservationSystem,
} from './realEstate.types';

export enum SaunaType {
  /** 1 dressing room, 1 shower room and 1 sauna */
  SAUNA_WITH_1_ROOM_AND_1_SHOWER = 'SAUNA_WITH_1_ROOM_AND_1_SHOWER',
  /** 2 dressing rooms, 2 shower rooms and 1 sauna */
  SAUNA_WITH_2_ROOMS_AND_2_SHOWERS = 'SAUNA_WITH_2_ROOMS_AND_2_SHOWERS',
  /** 2 dressing rooms, 1 shower room and 1 sauna */
  SAUNA_WITH_2_ROOMS_AND_1_SHOWER = 'SAUNA_WITH_2_ROOMS_AND_1_SHOWER',
}

export enum ReservationType {
  /** Private sauna reservation */
  CONTINUOUS_PRIVATE_RESERVATION = 'CONTINUOUS_PRIVATE_RESERVATION',
  /** Shared public sauna */
  CONTINUOUS_PUBLIC_RESERVATION = 'CONTINUOUS_PUBLIC_RESERVATION',
  NOT_RESERVABLE = 'NOT_RESERVABLE',
}

/**
 * Weekdays as they come from the API. Starts from Sunday so that
 * we can easily use them with date-fns format functions
 * where week starts from Sunday
 */
export enum WeekdayKey {
  SUNDAY = 'SUNDAY',
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
}

/** An individual sauna */
export interface SaunaResource {
  resourceId: string;
  name: string;
  description: string;
  descriptionEng: string;
  /** Defines sauna features (amount of showers and rooms) */
  type: SaunaType;
  /** Length of a sauna shift, in minutes */
  shiftLength: number;
  /** Price in eur */
  price: number;
  reservationSystem: FacilityReservationSystem;
  reservationParty: FacilityReservationParty;
}

export interface SaunaWithReservations extends SaunaResource {
  reservations: SaunaShift[];
  sharedShifts: SaunaShift[];
}

/** Shifts listed in the sauna reservation calendar */
export interface ScheduleShift {
  /** Reservation id that matches a SaunaReservation */
  reservationId: string | null;
  description?: string;
  startDateTime: SerializedPlainDateTime;
  endDateTime: SerializedPlainDateTime;
  /** Is this shift already reserved? */
  reserved: boolean;
}

/** An already reserved sauna shift */
export interface SaunaShift {
  /** Start time of a reservation, as  `HH:mm` */
  startTime: SerializedTime;
  /** End time of a reservation, as `HH:mm` */
  endTime: SerializedTime;
  /** End date of a reservation, as `yyyy-MM-dd` */
  startDate: SerializedPlainDate;
  /** Start date of a reservation, as `yyyy-MM-dd` */
  endDate: SerializedPlainDate | null;
  reservationId: string;
  /** Sauna resource id */
  resourceId: string;
  type: ReservationType;
  /** Length of the shift, in minutes */
  shiftLength: number;
  /** Price of the shift, in EUR */
  price: number;
  weekDay: WeekdayKey;
  /** Name of the shift. Used as a fallback for `description` */
  name: string | null;
  /** Description of the shift, usually describing which dressing room and/or sauna is reserved. */
  description: string | null;
}

export type SaunaReservationRequest = {
  /** Id of the sauna */
  resourceId: string;
  apartmentId: string;
  startDate: SerializedPlainDate;
  startTime: SerializedTime;
  endTime: SerializedTime;
  shiftLength: number;
  reservationType: 'CONTINUOUS';
};

export interface ReserveShiftResponse {
  createdReservationId: string;
  resourceId: string;
}

export type SaunaReservationChangeRequest = {
  reservationId: string;

  startDate: SerializedPlainDate;
  endDate: SerializedPlainDate;

  startTime: SerializedTime;
  endTime: SerializedTime;
};
